import { ValueObject } from '../base/value-object'
import {
  ROLE_BROKER,
  ROLE_CLIENT_ADMIN,
  ROLE_CLIENT_READONLY_ADMIN,
  ROLE_EMPLOYEE,
  ROLE_HIXME_READONLY_ADMIN,
  ROLE_HIXME_SALES_REP,
  ROLE_PLATFORM_HIXME_ADMIN,
  ROLE_PROSPECT_ADMIN,
} from '@hixme/role-policy'
import { z } from 'zod'

export const RoleEnum = {
  PLATFORM_HIXME_ADMIN: ROLE_PLATFORM_HIXME_ADMIN,
  HIXME_READONLY_ADMIN: ROLE_HIXME_READONLY_ADMIN,
  HIXME_SALES_REP: ROLE_HIXME_SALES_REP,
  CLIENT_ADMIN: ROLE_CLIENT_ADMIN,
  CLIENT_READONLY_ADMIN: ROLE_CLIENT_READONLY_ADMIN,
  EMPLOYEE: ROLE_EMPLOYEE,
  PROSPECT_ADMIN: ROLE_PROSPECT_ADMIN,
  BROKER: ROLE_BROKER,
} as const

export const RoleSchema = z.nativeEnum(RoleEnum)
export type Role = z.infer<typeof RoleSchema>
export type RoleEnumValue = (typeof RoleEnum)[keyof typeof RoleEnum]

export class InvalidRoleError extends Error {
  constructor(readonly invalidValue: string) {
    super(`Invalid role: ${invalidValue}`)
    this.name = 'InvalidRoleError'
  }
}

export class RoleVO extends ValueObject<Role> {
  static readonly PLATFORM_HIXME_ADMIN = RoleEnum.PLATFORM_HIXME_ADMIN
  static readonly HIXME_READONLY_ADMIN = RoleEnum.HIXME_READONLY_ADMIN
  static readonly HIXME_SALES_REP = RoleEnum.HIXME_SALES_REP
  static readonly CLIENT_ADMIN = RoleEnum.CLIENT_ADMIN
  static readonly CLIENT_READONLY_ADMIN = RoleEnum.CLIENT_READONLY_ADMIN
  static readonly EMPLOYEE = RoleEnum.EMPLOYEE
  static readonly PROSPECT_ADMIN = RoleEnum.PROSPECT_ADMIN
  static readonly BROKER = RoleEnum.BROKER

  constructor(value: unknown) {
    super(value as Role)
  }

  protected validate(): void {
    const parsed = RoleSchema.safeParse(this._value)
    if (!parsed.success) {
      throw new InvalidRoleError(String(this._value))
    }
  }

  static fromString(value: string): RoleVO {
    return new RoleVO(value)
  }

  static fromEnum(value: RoleEnumValue): RoleVO {
    return new RoleVO(value)
  }

  is(role: RoleEnumValue): boolean {
    return this.value === role
  }

  isPlatformHixmeAdmin(): boolean {
    return this.is(RoleEnum.PLATFORM_HIXME_ADMIN)
  }

  isHixmeReadonlyAdmin(): boolean {
    return this.is(RoleEnum.HIXME_READONLY_ADMIN)
  }

  isHixmeSalesRep(): boolean {
    return this.is(RoleEnum.HIXME_SALES_REP)
  }

  isClientAdmin(): boolean {
    return this.is(RoleEnum.CLIENT_ADMIN)
  }

  isClientReadonlyAdmin(): boolean {
    return this.is(RoleEnum.CLIENT_READONLY_ADMIN)
  }

  isEmployee(): boolean {
    return this.is(RoleEnum.EMPLOYEE)
  }

  isProspectAdmin(): boolean {
    return this.is(RoleEnum.PROSPECT_ADMIN)
  }

  isBroker(): boolean {
    return this.is(RoleEnum.BROKER)
  }
}
